import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';

import HeroSection from './HeroSection';
import ProblemSection from './ProblemSection';
import FeaturesSection from './FeaturesSection';
import HowItWorksSection from './HowItWorksSection';
import AboutSection from './AboutSection';
import PricingSection from './PricingSection';
import FinalCTA from './FinalCTA';
import SiteFooter from './SiteFooter';
import ScrollToTopButton from '../common/ScrollToTopButton';

// Lazy-load AuthModal — saves ~17KB from initial bundle
const AuthModal = lazy(() => import('./AuthModal'));

// ── Sticky nav ────────────────────────────────────────────────────────────────
function SiteNav({ onSignIn, onSignUp, onTryDemo, isAuthenticated, onGoToDashboard, onStartPlanning, onSignOut, onShowPrivacy, onShowTerms, onShowFaq, user }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const navItems = [
    { label: 'Features', id: 'features' },
    { label: 'How it works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'About', id: 'about' },
  ];

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-40"
      style={{
        backgroundColor: 'rgba(253,251,247,0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--brand-border)',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-2">
        <img
          src="/new_icon+text_color.png"
          alt="CraftMyParty"
          className="h-10 sm:h-14 md:h-16 w-auto flex-shrink-0 max-w-[42%] sm:max-w-none object-contain"
          style={{ cursor: 'pointer' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        />
        <div className="hidden lg:flex items-center gap-1 rounded-full border border-[var(--brand-border)] bg-white/70 px-1 py-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollToSection(item.id)}
              className="rounded-full px-4 py-2 text-sm font-bold text-gray-600 transition hover:bg-stone-100 hover:text-stone-900"
            >
              {item.label}
            </button>
          ))}
          {onShowFaq && (
            <button
              type="button"
              onClick={onShowFaq}
              className="rounded-full px-4 py-2 text-sm font-bold text-gray-600 transition hover:bg-stone-100 hover:text-stone-900"
            >
              FAQ
            </button>
          )}
        </div>

        <div className="flex min-w-0 flex-shrink items-center gap-1 sm:gap-2">
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm font-medium text-gray-600">
                Hi, {user?.name?.split(' ')[0] || 'there'}
              </span>
              <button
                onClick={onGoToDashboard}
                className="px-4 sm:px-5 py-2 text-sm font-bold text-white rounded-full transition-all duration-200 hover:scale-105"
                style={{ backgroundColor: 'var(--brand-violet)', boxShadow: '0 2px 8px rgba(24,24,27,0.12)' }}
              >
                Go to Dashboard
              </button>
              {onSignOut && (
                <button
                  onClick={onSignOut}
                  className="hidden md:inline-block ml-1 text-sm font-bold text-red-600 hover:text-red-700 transition"
                >
                  Sign Out
                </button>
              )}
            </div>
          ) : (
            <>
              {onTryDemo && (
                <button
                  onClick={onTryDemo}
                  className="hidden lg:block px-4 py-2 text-sm font-semibold transition-colors duration-150"
                  style={{ color: 'var(--brand-muted)' }}
                  onMouseEnter={(e) => (e.target.style.color = 'var(--brand-violet)')}
                  onMouseLeave={(e) => (e.target.style.color = 'var(--brand-muted)')}
                >
                  Try Demo
                </button>
              )}
              {/* Compact "Sign in" pill — always visible (even on the
                  smallest screens) so signed-out visitors don't have to dig
                  through the hamburger menu to reach the auth flow. Uses the
                  brand-border ring styling so it doesn't crowd the logo. */}
              <button
                onClick={onSignIn}
                className="inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-[var(--brand-border)] bg-white px-2.5 text-xs font-semibold transition-colors duration-150 sm:h-10 sm:px-4 sm:text-sm"
                style={{ color: 'var(--brand-charcoal)' }}
                onMouseEnter={(e) => (e.target.style.color = 'var(--brand-violet)')}
                onMouseLeave={(e) => (e.target.style.color = 'var(--brand-charcoal)')}
              >
                Sign in
              </button>
              <button
                onClick={onSignUp}
                className="hidden sm:inline-flex px-4 sm:px-5 py-2 text-sm font-bold text-white rounded-full transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: 'var(--brand-violet)', boxShadow: '0 2px 8px rgba(24,24,27,0.12)' }}
              >
                Start Free
              </button>
            </>
          )}
          
          {/* Relative container for anchoring the compact menu on sm+ viewports */}
          <div ref={menuRef} className="relative inline-block text-left">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="lg:hidden ml-1 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--brand-border)] bg-white text-gray-800 shadow-sm"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </svg>
              )}
            </button>

            {mobileMenuOpen && (
              <div 
                className="lg:hidden absolute right-0 top-full mt-2 w-[calc(100vw-32px)] sm:w-64 border border-[var(--brand-border)] bg-white/95 rounded-2xl p-4 shadow-xl z-50 transition-all duration-200"
              >
                <div className="grid gap-1">
                  {/* Public Scroll Sections */}
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className="rounded-xl px-3 py-2 text-left text-sm font-bold text-gray-800 hover:bg-stone-100"
                    >
                      {item.label}
                    </button>
                  ))}
                  
                  <hr className="border-[var(--brand-border)] my-1" />

                  {isAuthenticated ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onGoToDashboard();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-800 hover:bg-stone-100"
                      >
                        Dashboard
                      </button>
                      {onStartPlanning && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            onStartPlanning();
                          }}
                          className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-800 hover:bg-stone-100"
                        >
                          Start new plan
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onShowPrivacy();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                      >
                        Privacy Policy
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onShowTerms();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                      >
                        Terms of Service
                      </button>
                      {onShowFaq && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            onShowFaq();
                          }}
                          className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                        >
                          FAQ
                        </button>
                      )}
                      {onSignOut && (
                        <>
                          <hr className="border-[var(--brand-border)] my-1" />
                          <button
                            type="button"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              onSignOut();
                            }}
                            className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-red-600 hover:bg-red-50"
                          >
                            Sign Out
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onSignIn();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-stone-900 hover:bg-stone-100"
                      >
                        Sign In
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onSignUp();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-stone-900 hover:bg-stone-100"
                      >
                        Start Planning
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onShowPrivacy();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                      >
                        Privacy Policy
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onShowTerms();
                        }}
                        className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                      >
                        Terms of Service
                      </button>
                      {onShowFaq && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            onShowFaq();
                          }}
                          className="rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-500 hover:bg-stone-100"
                        >
                          FAQ
                        </button>
                      )}
                      {onTryDemo && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            onTryDemo();
                          }}
                          className="rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-800 hover:bg-stone-100"
                        >
                          Try demo
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

// ── Landing Page ──────────────────────────────────────────────────────────────
const LandingPage = ({ onEnterApp, onShowPrivacy, onShowTerms, onShowFaq, isAuthenticated, onGoToDashboard, onStartPlanning, onSignOut, user, onAuthIntent, onGoToBilling }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const howItWorksRef = useRef(null);

  // Reset scroll position before first paint
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, []);

  const openSignIn = () => { 
    onAuthIntent?.('signin');
    setAuthMode('signin'); 
    setShowAuthModal(true); 
  };
  const openSignUp = () => { 
    onAuthIntent?.('startPlanning');
    setAuthMode('signup'); 
    setShowAuthModal(true); 
  };

  const handleEnterApp = () => {
    onAuthIntent?.('demo');
    onEnterApp();
  };

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-clip" style={{ backgroundColor: 'var(--brand-cream)' }}>
      <SiteNav 
        onSignIn={openSignIn} 
        onSignUp={openSignUp} 
        onTryDemo={handleEnterApp} 
        isAuthenticated={isAuthenticated} 
        onGoToDashboard={onGoToDashboard} 
        onStartPlanning={onStartPlanning}
        onSignOut={onSignOut}
        onShowPrivacy={onShowPrivacy}
        onShowTerms={onShowTerms}
        onShowFaq={onShowFaq}
        user={user} 
      />

      <main className="w-full max-w-full overflow-x-clip">
        <HeroSection
          onStartPlanning={isAuthenticated ? onStartPlanning : openSignUp}
          onScrollToHowItWorks={scrollToHowItWorks}
          onTryDemo={isAuthenticated ? null : handleEnterApp}
          isAuthenticated={isAuthenticated}
          user={user}
        />

        <ProblemSection />

        <FeaturesSection />

        <HowItWorksSection sectionRef={howItWorksRef} />

        {/*
          Testimonials placeholder — add when you have real user quotes.
          <TestimonialsSection />
        */}

        <AboutSection />
        <PricingSection onSignUp={isAuthenticated ? onGoToBilling : openSignUp} isAuthenticated={isAuthenticated} />
        <FinalCTA onStartPlanning={isAuthenticated ? onStartPlanning : openSignUp} isAuthenticated={isAuthenticated} />
      </main>

      <SiteFooter
        onShowPrivacy={onShowPrivacy}
        onShowTerms={onShowTerms}
        onEnterApp={handleEnterApp}
        onShowFaq={onShowFaq}
      />

      {/* Lazy-loaded auth modal */}
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal
            mode={authMode}
            onClose={() => setShowAuthModal(false)}
            onSwitchMode={setAuthMode}
            onSuccess={() => setShowAuthModal(false)}
          />
        </Suspense>
      )}

      <ScrollToTopButton />
    </div>
  );
};

export default LandingPage;
